/**
 * Custom hook for WebSocket dynamic segmentation events
 * Provides real-time segmentation functionality with progress tracking
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import socketService, { SegmentationEvent } from '../services/SocketService';
import { segmentationService, SegmentationParams, SegmentationResponse, DynamicSegment, Segmentation } from '../services/segmentationService';

interface SegmentationState {
  isConnected: boolean;
  isProcessing: boolean;
  progress: number;
  currentStep: string | null;
  segments: DynamicSegment[];
  pendingSegments: DynamicSegment[] | null;  // Segment definitions before counts are ready
  error: string | null;
  taskId: string | null;
  segmentationId: number | null;
  cached: boolean;
  expectedSegmentCount: number | null;  // For skeleton UI
  assignmentProgress: { completed: number; total: number } | null;  // Assignment tracking
}

interface UseSegmentationSocketOptions {
  onProcessingStarted?: (data: SegmentationEvent) => void;
  onProgressUpdate?: (data: SegmentationEvent) => void;
  onProcessingCompleted?: (data: SegmentationEvent) => void;
  onProcessingFailed?: (data: SegmentationEvent) => void;
  onConnectionStatus?: (connected: boolean) => void;
  autoConnect?: boolean;
}

interface UseSegmentationSocketReturn {
  segmentationState: SegmentationState;
  startSegmentation: (params: SegmentationParams) => Promise<SegmentationResponse>;
  fetchSegmentation: (id: number) => Promise<Segmentation>;
  listSegmentations: (audienceId: number) => Promise<Segmentation[]>;
  cancelSegmentation: () => void;
  clearState: () => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  isConnected: boolean;
}

const useSegmentationSocket = (
  options: UseSegmentationSocketOptions = {}
): UseSegmentationSocketReturn => {
  const {
    onProcessingStarted,
    onProgressUpdate,
    onProcessingCompleted,
    onProcessingFailed,
    onConnectionStatus,
    autoConnect = false
  } = options;

  const [segmentationState, setSegmentationState] = useState<SegmentationState>({
    isConnected: false,
    isProcessing: false,
    progress: 0,
    currentStep: null,
    segments: [],
    pendingSegments: null,
    error: null,
    taskId: null,
    segmentationId: null,
    cached: false,
    expectedSegmentCount: null,
    assignmentProgress: null
  });

  const activeSegmentationIdRef = useRef<number | null>(null);
  const subscriberIdRef = useRef<string | null>(null);

  // Handle segmentation started event
  const handleSegmentationStarted = useCallback((data: SegmentationEvent) => {
    if (data.segmentation_id === activeSegmentationIdRef.current) {
      setSegmentationState(prev => ({
        ...prev,
        isProcessing: true,
        progress: 0,
        currentStep: 'Starting segmentation...',
        error: null,
        segmentationId: data.segmentation_id
      }));
      onProcessingStarted?.(data);
    }
  }, [onProcessingStarted]);

  // Handle segmentation progress event
  const handleSegmentationProgress = useCallback((data: SegmentationEvent) => {
    if (data.segmentation_id === activeSegmentationIdRef.current) {
      setSegmentationState(prev => ({
        ...prev,
        progress: data.progress || 0,
        currentStep: data.status || data.message || null,  // Backend sends 'status', fallback to 'message'
        // Update expected segment count if provided (for skeleton UI)
        expectedSegmentCount: data.expected_segment_count ?? prev.expectedSegmentCount,
        // Capture segment definitions early for immediate UI display
        pendingSegments: data.segment_definitions ?? prev.pendingSegments,
        // Track assignment progress for detailed progress display
        assignmentProgress: data.assignment_progress ?? prev.assignmentProgress
      }));
      onProgressUpdate?.(data);
    }
  }, [onProgressUpdate]);

  // Handle segmentation completed event
  const handleSegmentationCompleted = useCallback((data: SegmentationEvent) => {
    if (data.segmentation_id === activeSegmentationIdRef.current) {
      setSegmentationState(prev => ({
        ...prev,
        isProcessing: false,
        progress: 100,
        currentStep: 'Segmentation completed',
        segments: data.segment_definitions || [],
        pendingSegments: null,  // Clear pending now that we have final results
        assignmentProgress: null,
        error: null,
        segmentationId: data.segmentation_id
      }));
      activeSegmentationIdRef.current = null;
      onProcessingCompleted?.(data);
    }
  }, [onProcessingCompleted]);

  // Handle segmentation failed event
  const handleSegmentationFailed = useCallback((data: SegmentationEvent) => {
    if (data.segmentation_id === activeSegmentationIdRef.current) {
      setSegmentationState(prev => ({
        ...prev,
        isProcessing: false,
        progress: 0,
        currentStep: null,
        error: data.error || 'Segmentation failed',
        segments: []
      }));
      activeSegmentationIdRef.current = null;
      onProcessingFailed?.(data);
    }
  }, [onProcessingFailed]);

  // Handle connection status changes
  const handleConnectionStatus = useCallback((connected: boolean) => {
    setSegmentationState(prev => ({
      ...prev,
      isConnected: connected
    }));
    onConnectionStatus?.(connected);
  }, [onConnectionStatus]);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    try {
      await socketService.connect();

      // Clean up any existing subscription
      if (subscriberIdRef.current) {
        socketService.unsubscribeSubscriber(subscriberIdRef.current);
        subscriberIdRef.current = null;
      }

      // Subscribe to segmentation events
      const subscriberId = socketService.subscribeToEvents({
        onSegmentationStarted: handleSegmentationStarted,
        onSegmentationProgress: handleSegmentationProgress,
        onSegmentationCompleted: handleSegmentationCompleted,
        onSegmentationFailed: handleSegmentationFailed
      });
      subscriberIdRef.current = subscriberId;

      setSegmentationState(prev => ({ ...prev, isConnected: true }));
      handleConnectionStatus(true);
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      setSegmentationState(prev => ({ ...prev, isConnected: false }));
      handleConnectionStatus(false);
    }
  }, [
    handleSegmentationStarted,
    handleSegmentationProgress,
    handleSegmentationCompleted,
    handleSegmentationFailed,
    handleConnectionStatus
  ]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (subscriberIdRef.current) {
      socketService.unsubscribeSubscriber(subscriberIdRef.current);
      subscriberIdRef.current = null;
    }
    setSegmentationState(prev => ({ ...prev, isConnected: false }));
    handleConnectionStatus(false);
  }, [handleConnectionStatus]);

  // Start segmentation
  const startSegmentation = useCallback(async (
    params: SegmentationParams
  ): Promise<SegmentationResponse> => {
    try {
      // Clear previous state
      setSegmentationState(prev => ({
        ...prev,
        isProcessing: true,
        progress: 0,
        currentStep: 'Initializing...',
        error: null,
        segments: [],
        pendingSegments: null,
        cached: false,
        expectedSegmentCount: null,
        assignmentProgress: null
      }));

      const response = await segmentationService.create(params);

      // Store the segmentation ID
      activeSegmentationIdRef.current = response.segmentation_id;

      // Check if result is cached (sync response)
      if (response.cached || response.segments) {
        setSegmentationState(prev => ({
          ...prev,
          isProcessing: false,
          progress: 100,
          currentStep: 'Loaded from cache',
          segments: response.segments || [],
          segmentationId: response.segmentation_id,
          cached: response.cached || false
        }));
        activeSegmentationIdRef.current = null;
      } else if (response.task_id) {
        // Async processing - update state
        setSegmentationState(prev => ({
          ...prev,
          taskId: response.task_id || null,
          segmentationId: response.segmentation_id
        }));

        // Start polling if in polling mode
        if (socketService.isPollingMode()) {
          socketService.startPollingForSegmentation(response.segmentation_id);
        }
      }

      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start segmentation';
      setSegmentationState(prev => ({
        ...prev,
        error: errorMessage,
        isProcessing: false,
        progress: 0,
        currentStep: null
      }));
      throw error;
    }
  }, []);

  // Fetch a specific segmentation
  const fetchSegmentation = useCallback(async (id: number): Promise<Segmentation> => {
    const segmentation = await segmentationService.get(id);
    if (segmentation.status === 'completed') {
      setSegmentationState(prev => ({
        ...prev,
        segments: segmentation.segment_definitions,
        segmentationId: segmentation.id
      }));
    }
    return segmentation;
  }, []);

  // List segmentations for an audience
  const listSegmentations = useCallback(async (audienceId: number): Promise<Segmentation[]> => {
    return segmentationService.listForAudience(audienceId);
  }, []);

  // Cancel segmentation (just stops polling)
  const cancelSegmentation = useCallback(() => {
    if (activeSegmentationIdRef.current && socketService.isPollingMode()) {
      socketService.stopPollingForSegmentation(activeSegmentationIdRef.current);
    }
    activeSegmentationIdRef.current = null;
    setSegmentationState(prev => ({
      ...prev,
      isProcessing: false,
      progress: 0,
      currentStep: null
    }));
  }, []);

  // Clear state
  const clearState = useCallback(() => {
    if (activeSegmentationIdRef.current && socketService.isPollingMode()) {
      socketService.stopPollingForSegmentation(activeSegmentationIdRef.current);
    }
    activeSegmentationIdRef.current = null;
    setSegmentationState({
      isConnected: segmentationState.isConnected,
      isProcessing: false,
      progress: 0,
      currentStep: null,
      segments: [],
      pendingSegments: null,
      error: null,
      taskId: null,
      segmentationId: null,
      cached: false,
      expectedSegmentCount: null,
      assignmentProgress: null
    });
  }, [segmentationState.isConnected]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    segmentationState,
    startSegmentation,
    fetchSegmentation,
    listSegmentations,
    cancelSegmentation,
    clearState,
    connect,
    disconnect,
    isConnected: segmentationState.isConnected
  };
};

export default useSegmentationSocket;
export type { SegmentationState, UseSegmentationSocketOptions, UseSegmentationSocketReturn };
